import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const words = ["COMUNICA", "IMPRESSIONA", "CONVERTE"];
const WORD_DURATION = 1600; // ms each word is visible

const Preloader = ({ onComplete }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(-1); // Start with no word visible
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timers = [];
    const INITIAL_DELAY = 400; // Brief pause before first word appears

    // Schedule each word including the first one
    for (let i = 0; i < words.length; i++) {
      timers.push(
        setTimeout(() => setCurrentWordIndex(i), INITIAL_DELAY + i * WORD_DURATION)
      );
    }

    // Exit after all words shown
    timers.push(
      setTimeout(() => setIsExiting(true), INITIAL_DELAY + words.length * WORD_DURATION)
    );

    return () => timers.forEach(clearTimeout);
  }, []);

  const containerVariants = {
    exit: {
      transition: {
        staggerChildren: 0.18,
        staggerDirection: 1
      }
    }
  };

  const columnVariants = {
    initial: { y: '0%' },
    exit: {
      y: '-100%',
      transition: {
        duration: 1,
        ease: [0.76, 0, 0.24, 1]
      }
    }
  };

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {!isExiting && (
        <motion.div
          className="fixed inset-0 z-[9999] pointer-events-auto overflow-hidden grid grid-cols-2 lg:grid-cols-4"
          variants={containerVariants}
          initial="initial"
          exit="exit"
        >
          {[0, 1, 2, 3].map((i) => {
            // Responsive Tailwind classes to hide outer columns on mobile/tablet
            let colClass = "relative will-change-transform";
            if (i === 0) {
              colClass += " hidden lg:block border-r border-[#FF5A1F]";
            } else if (i === 1) {
              colClass += " border-r border-[#FF5A1F]";
            } else if (i === 2) {
              colClass += " border-r-0 lg:border-r border-[#FF5A1F]";
            } else if (i === 3) {
              colClass += " hidden lg:block";
            }

            return (
              <motion.div
                key={i}
                className={colClass}
                style={{
                  height: '100%',
                  background: '#000',
                }}
                variants={columnVariants}
              >
                {/* Column 2 (index 1): Static "Design que:" */}
                {i === 1 && (
                  <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    style={{ zIndex: 2 }}
                  >
                    <span
                      className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/80"
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontStyle: 'italic',
                      }}
                    >
                      Design que:
                    </span>
                  </div>
                )}

                {/* Column 3 (index 2): Words — pure CSS transitions, no AnimatePresence */}
                {i === 2 && (
                  <div
                    className="absolute inset-0 flex items-center px-4 md:px-8 pointer-events-none"
                    style={{ zIndex: 2 }}
                  >
                    {words.map((word, wi) => (
                      <span
                        key={wi}
                        className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-display font-bold leading-none tracking-[0.12em] md:tracking-[0.2em] absolute"
                        style={{
                          color: '#FF5A1F',
                          transform: currentWordIndex === wi ? 'translateY(0)' : (currentWordIndex > wi ? 'translateY(-80px)' : 'translateY(80px)'),
                          opacity: currentWordIndex === wi ? 1 : 0,
                          transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                          willChange: 'transform, opacity',
                        }}
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
